import React from 'react';
import { Link } from 'react-router-dom';

export const meta = {
  slug: 'what-is-a-section-32',
  title: 'What is a Section 32? A plain-English guide for Victorian buyers',
  navTitle: 'What is a Section 32?',
  description:
    'The Section 32 vendor statement is the disclosure document every Victorian property seller must give you before you sign. Here’s what’s inside, what’s not, and why it matters.',
  published: '2026-07-14',
  readingTime: '6 min read',
  teaser:
    'The disclosure document behind every Victorian property sale — what it must contain, what it deliberately leaves out, and what happens if it’s wrong.',
};

export default function WhatIsASection32() {
  return (
    <>
      <p className="guide-lede">
        If you're buying property in Victoria, the Section 32 is the single most important
        document you'll receive before signing anything. It's the vendor's legally required
        disclosure statement — and it's usually the first place problems show up, if you
        know where to look.
      </p>

      <h2>The short answer</h2>
      <p>
        A Section 32 — formally a <strong>vendor statement</strong> — is a document the
        seller of a Victorian property must give a buyer <em>before</em> the buyer signs the
        contract of sale. It takes its name from section 32 of the{' '}
        <em>Sale of Land Act 1962</em> (Vic), which lists exactly what the vendor has to
        disclose about the property.
      </p>
      <p>
        The idea is simple: some things about a property can't be seen at an open
        inspection. Easements under the back yard, an owners corporation with a special
        levy looming, a road-widening proposal, unpermitted building works — the Section 32
        is where these must be put in writing before you commit.
      </p>

      <h2>What's inside a Section 32</h2>
      <p>
        The exact contents vary with the property, but a Section 32 generally includes:
      </p>
      <ul>
        <li>
          <strong>Title details</strong> — a copy of the Register Search Statement showing
          who owns the property, the lot and plan number, and any mortgages, caveats or
          other interests registered on the title.
        </li>
        <li>
          <strong>Encumbrances, easements and covenants</strong> — rights that other
          parties hold over the land, like a sewerage easement in favour of the water
          authority, or a restrictive covenant limiting what can be built. (Our{' '}
          <Link to="/guides/easements-explained">easements guide</Link> unpacks these.)
        </li>
        <li>
          <strong>The plan of subdivision</strong> — the surveyed drawing showing your
          lot's boundaries, dimensions and any easement strips.
        </li>
        <li>
          <strong>Planning information</strong> — the zone the property sits in and any
          planning overlays (heritage, bushfire management, flooding, and so on) that
          restrict how the land can be used or developed.
        </li>
        <li>
          <strong>Outgoings</strong> — council rates, water charges, owners corporation
          fees and any other periodic amounts the owner has to pay.
        </li>
        <li>
          <strong>Building permits from the last seven years</strong> — plus, where the
          vendor was an owner-builder, details of required warranty insurance for their
          works.
        </li>
        <li>
          <strong>Notices and orders</strong> — anything issued by a government authority
          that affects the land, such as compulsory acquisition proposals or building
          notices.
        </li>
        <li>
          <strong>Services</strong> — which of electricity, gas, water, sewerage and
          telephone are <em>not</em> connected to the property.
        </li>
        <li>
          <strong>Owners corporation certificate</strong> — for apartments, units and many
          townhouses: the OC's fees, insurance, funds and any disputes or planned works.
        </li>
        <li>
          <strong>Other statutory disclosures</strong> — such as whether the land is in a
          designated bushfire-prone area, or subject to a Growth Areas Infrastructure
          Contribution (GAIC) in Melbourne's growth corridors.
        </li>
      </ul>

      <h2>What a Section 32 does NOT tell you</h2>
      <p>
        Just as important is what the vendor statement is <em>not</em> required to say.
        A Section 32 generally will not tell you:
      </p>
      <ul>
        <li>
          <strong>The condition of the building.</strong> Structural issues, rising damp,
          termites, dodgy wiring — none of that belongs to the Section 32. That's what a
          building and pest inspection is for.
        </li>
        <li>
          <strong>Whether existing structures were ever approved.</strong> The statement
          lists permits issued in the last seven years; an unpermitted carport from 2003
          simply won't appear.
        </li>
        <li>
          <strong>What the property is worth</strong>, or anything about comparable sales.
        </li>
        <li>
          <strong>Most things about the neighbours</strong> — planned developments next
          door usually aren't disclosable unless a formal notice affects the land itself.
        </li>
      </ul>
      <p>
        Treat the Section 32 as one of three legs: the vendor statement for legal
        disclosures, a building and pest inspection for physical condition, and the
        contract of sale (with its special conditions) for the terms of the deal.
      </p>

      <h2>Who prepares it, and when you get it</h2>
      <p>
        The vendor's conveyancer or solicitor prepares the Section 32, and the estate
        agent typically sends it — bundled with the contract of sale — when you ask for
        "the contract". You're entitled to see it before you sign, and a vendor who fails
        to provide one (or provides a defective one) risks the buyer walking away.
      </p>

      <h2>What if the Section 32 is wrong or incomplete?</h2>
      <p>
        If the vendor statement is missing required information or contains false
        information, a buyer may be able to withdraw from the contract before settlement —
        even after signing. There are exceptions (broadly, where the vendor acted honestly
        and reasonably and the buyer is in substantially the same position), so this is
        squarely a talk-to-your-conveyancer situation. The practical takeaway: problems
        found <em>before</em> signing are negotiating leverage; problems found after are
        legal questions.
      </p>

      <h2>Don't forget the cooling-off rules</h2>
      <p>
        For private sales of residential property in Victoria, buyers generally get{' '}
        <strong>three clear business days</strong> after signing to cool off, at a small
        cost. But there are significant exceptions — most notably auctions, and purchases
        within three clear business days either side of a publicly advertised auction.
        If you're buying at or around an auction, assume there is <em>no</em> cooling-off
        and get everything checked beforehand.
      </p>

      <h2>How long does it take to read one?</h2>
      <p>
        A Section 32 for a suburban house typically runs 50–150 pages once the title
        search, plan, certificates and rate notices are attached; apartment vendor
        statements with owners corporation certificates can be longer. Most buyers skim
        the first few pages and hope. The certificates at the back — water authority
        searches, planning certificates, OC statements — are where easements, overlays and
        fees actually live.
      </p>
    </>
  );
}
